// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : some wiki utils
// =============================================================================
// ---Third-party imports
import React, {useContext, createContext} from 'react'

import {isFunction, isString, dict, filterItems, mapItems, isNil, has} from 'ldash'

// ---Framework imports
import {decodeAttribH5, MIME} from 'uix/utils'
import log from 'uix/logging'

// ---Exports
export {decodeAttribH5, MIME}
export const BOOL_STR = ['true', 'false']
export const bools = names => dict(names.map(n => [n, BOOL_STR]))

export const MIMETYPES = {
  [MIME.XHTML]: 'XHTML',
  [MIME.HTML]: 'HTML5',
  [MIME.XML]: 'XML',
  [MIME.MARKDOWN]: 'Markdown',
  [MIME.HOCR]: 'HOCR',
  [MIME.TEI]: 'TEI',
  [MIME.XSLT]: 'XSLT',
}

export const SUI_COLORS = [
  'red',
  'orange',
  'yellow',
  'olive',
  'green',
  'teal',
  'blue',
  'violet',
  'purple',
  'pink',
  'brown',
  'grey',
  'black',
]
export const SUI_SIZES = ['mini', 'tiny', 'small', 'medium', 'large', 'big', 'huge', 'massive']
export const SUI_ICONS = ['list', 'play']

export const SUI_MENU_ITEM_ATTRS = {
  position: ['left', 'right'],
  fitted: ['true', 'false', 'horizontally', 'vertically'],
  icon: SUI_ICONS,
  color: SUI_COLORS,
  className: [],
  content: [],
  size: SUI_SIZES,
  ...bools(['active', 'disabled', 'link']),
}

export const GenericTagHandler = (tagname, render_f, completionAttrs, docs) => {
  /* create matcher for simple tagname
  only enabled if tagname is found in wiki code
   */
  if (isFunction(render_f)) {
    render_f.displayName = tagname
  }

  if (isString(completionAttrs)) {
    // allow 3-param form with no completionAttrs but with docs
    docs = completionAttrs
    completionAttrs = {}
  }
  let completion = null
  if (!isFunction(completionAttrs)) {
    completionAttrs = dict(mapItems(completionAttrs, (k, v) => [k, isString(v) ? [v] : v]))
    completion = () => ({'!top': [tagname], [tagname]: {attrs: completionAttrs || {}}})
  } else {
    completion = completionAttrs
  }
  return {
    match: ({tag}) => tag === tagname,
    build: render_f,
    completion,
    enabled: initParams => initParams.keys.byTag[tagname],
    docs,
    tagMatcher: tagname,
  }
}

export const GenericTagHandlerWithAttrFilter = (tagname, render_f, completionAttrs, docs) =>
  /* as GenericTagHandler but:
  - transforms attrib with html5 decoding rules
  - filters attrib by known completionAttrs
  */
  GenericTagHandler(
    tagname,
    ({attrib, ...props}) => {
      attrib = dict(filterItems(decodeAttribH5(attrib), name => completionAttrs[name]))
      return render_f({attrib, ...props})
    },
    completionAttrs,
    docs
  )

export const TagMatcher = (tagname, render_f, completionAttrs, docs) => {
  /* as GenericTagHandler but:
  - creates ReactComp (you can directly use hooks)
  - transforms attrib with html5 decoding rules and pushes the directly into props
  */
  let {build, ...args} = GenericTagHandler(tagname, render_f, completionAttrs, docs)
  if (isFunction(build)) {
    var create_el = ({
      attrib,
      text,
      applyTemplates,
      parent,
      children,
      iconfig,
      dconfig,
      data,
      wikipaneId,
      util,
    }) =>
      React.createElement(build, {
        iconfig,
        dconfig,
        i18n: data.i18n,
        user: data.user,
        apiurl: data.apiurl,
        wikiIdentifier: data.identifier,
        wikipaneId,
        util,
        // pass through text
        text,
        // and applyTemplates
        applyTemplates,
        childEls: children, // children clashes with react internals
        parentEl: parent, // also rename as above
        // all attributes from tag
        ...decodeAttribH5(attrib),
      })
  } else {
    // assume build (render_f) is already a static ReactElement
    var create_el = () => build
  }

  return {
    build: create_el,
    ...args,
  }
}

// transport wiki properties into wiki templates
// otid, identifier, assetsById
export const WikiContext = createContext({})
export const useWikiContext = () => useContext(WikiContext)

export const schemaifyAttributes = (attrib, schema) => {
  attrib = decodeAttribH5(attrib)

  attrib = dict(
    mapItems(schema.byNameLowerCase, (name_lower, schemaitem) => {
      // hmm, both forms? resolved attribs from OW_Meta are currently cased
      let value = attrib[name_lower] ?? attrib[schemaitem.name]

      let {type, name} = schemaitem
      if (isNil(value) && has(schemaitem, 'default')) {
        value = schemaitem.default
      }
      if (isString(value)) {
        if (type == 'int') {
          value = parseInt(value)
        } else if (type == 'boolean') {
          value = value == 'true'
        }
      }
      return [name, value]
    })
  )

  return attrib
}
